import React, { memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { getTextField } from '@lender/utils';
import { DialogContentText } from '@mui/material';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import { AutocompleteCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';
import { object } from 'yup';

import useForm from 'hooks/useForm';
import useLoading from 'hooks/useLoading';

import { selectFannieLendersOptionsByPropertyName } from 'store/companies/selectors';

import { LoanHeadersTestIds } from '../../LoanHeaders.testIds';

const FannieDoSystemProperty = {
  LENDERS: 'FannieDOLenders',
  USERNAME: 'FannieDOUsername',
};

const schema = object().shape({
  fannieLender: getTextField(),
});

const FannieLenderForm = ({ formRef = null, onFormSubmit }) => {
  const [isLoading, setIsLoading] = useLoading(false);

  const fannieLendersOptions = useSelector(
    selectFannieLendersOptionsByPropertyName(FannieDoSystemProperty.LENDERS),
  );

  const { form, setSchema, setInForm, validateField, validateForm } = useForm({});

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      setIsLoading(true);

      onFormSubmit({ fannieDoLenderId: form.fannieLender?.value }).finally(() =>
        setIsLoading(false),
      );
    },
    [validateForm, setIsLoading, onFormSubmit, form],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="loan-editor-action-fields-form"
      isFormChanged={!!form.fannieLender}
      isSubmitting={isLoading}
      submitButtonName="Continue"
      onSubmit={onSubmit}
    >
      <DialogContentText>
        Please select a lender and confirm your choice. Note that this action is final and cannot be
        reversed.
      </DialogContentText>
      <AutocompleteCDS
        fullWidth
        containerClassName="mb-24"
        dataTestId={LoanHeadersTestIds.ASSOCIATE_REPORT_FORM_STATE_INPUT}
        disabled={isLoading}
        id="fannieLender"
        label="Fannie Lender"
        name="fannieLender"
        options={fannieLendersOptions}
        type="text"
        value={form.fannieLender || ''}
        variant="outlined"
        onChange={onAutocompleteChange}
      />
    </ModalFormWrapper>
  );
};

FannieLenderForm.propTypes = {
  onFormSubmit: PropTypes.func.isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
};

export default memo(FannieLenderForm);
